import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LANGS, setLang, t, getLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Globe, LogOut, LayoutDashboard, BookOpen } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [lang, setL] = useState(getLang());

  useEffect(() => {
    const h = () => setL(getLang());
    window.addEventListener("lang-change", h);
    return () => window.removeEventListener("lang-change", h);
  }, []);

  return (
    <div className="min-h-screen grain">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={user ? "/categories" : "/"} className="flex items-center gap-2.5" data-testid="brand-link">
            <img src="/godrive-mark.png" alt="Go Drive TVDE" className="h-8 w-auto" />
            <span className="font-serif font-bold text-xl tracking-tight hidden sm:inline">Go Drive <span className="text-[hsl(var(--accent))]">TVDE</span></span>
          </Link>
          <nav className="flex items-center gap-2">
            {user && (
              <>
                <Button variant="ghost" onClick={() => nav("/dashboard")} data-testid="nav-dashboard" className="hidden sm:inline-flex">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> {t(lang, "dashboard")}
                </Button>
                <Button variant="ghost" onClick={() => nav("/categories")} data-testid="nav-categories" className="hidden sm:inline-flex">
                  <BookOpen className="w-4 h-4 mr-2" /> {t(lang, "categories")}
                </Button>
                {user.is_admin && (
                  <Button variant="ghost" onClick={() => nav("/admin")} data-testid="nav-admin" className="hidden sm:inline-flex text-[hsl(var(--accent))]">
                    Admin
                  </Button>
                )}
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="lang-switcher" className="gap-1.5">
                  <Globe className="w-4 h-4" /> {LANGS.find(x => x.code === lang)?.flag} <span className="hidden sm:inline">{LANGS.find(x => x.code === lang)?.code.toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGS.map(l => (
                  <DropdownMenuItem key={l.code} onClick={() => { setLang(l.code); }} data-testid={`lang-${l.code}`}>
                    <span className="mr-2">{l.flag}</span> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {user ? (
              <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="btn-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login">{t(lang, "login")}</Button>
                <Button size="sm" onClick={() => nav("/apply")} data-testid="nav-apply" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">{t(lang, "apply")}</Button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="relative z-10">{children}</main>
      <WhatsAppButton />
      <footer className="border-t border-border/60 mt-24 py-8 text-center text-sm text-muted-foreground">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>© 2026 Go Drive TVDE · <span className="font-serif italic">Aprende. Conduz. Cresce.</span></span>
          <span className="font-mono text-xs">316 · pt / en / ur</span>
        </div>
      </footer>
    </div>
  );
}
